import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useQuery } from "@tanstack/react-query";
import { Link as RouterLink } from "react-router-dom";
import Link from "@mui/material/Link";
import { api, type CrmActivityItem, type CrmPerson, type ProspectDetail } from "../../api/client.ts";
import { keys } from "../../api/queryKeys.ts";
import { ORG_CRM_READ_SCOPE } from "../../auth/orgRoutes.ts";
import { userHasOrgScope } from "../../auth/scopes.ts";
import { useAuth } from "../../contexts/AuthContext.tsx";
import { useOrganization } from "../../contexts/OrganizationContext.tsx";
import { useTenant } from "../../contexts/TenantContext.tsx";
import { useState } from "react";
import { humanize } from "../../utils/format.ts";
import CrmPresenceChip, { CrmPresenceDetails } from "../CrmPresenceChip.tsx";
import RelativeTime from "../RelativeTime.tsx";
import SectionHeading from "../SectionHeading.tsx";

function ActivityRow({ activity: a }: { activity: CrmActivityItem }) {
  return (
    <Box sx={{ py: 0.75 }}>
      <Stack direction="row" spacing={1} sx={{ alignItems: "baseline" }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {humanize(a.channel)}
        </Typography>
        {a.connected && <Chip size="small" variant="outlined" color="success" label="Connected" />}
        <Typography variant="caption" sx={{ color: "text.secondary", flexGrow: 1 }}>
          {a.rep_name}
        </Typography>
        <Typography variant="caption" sx={{ color: "text.secondary", whiteSpace: "nowrap" }}>
          <RelativeTime value={a.occurred_at} />
        </Typography>
      </Stack>
      {(a.subject || a.body_preview) && (
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          {[a.subject, a.body_preview].filter(Boolean).join(" — ")}
        </Typography>
      )}
    </Box>
  );
}

function PersonRow({ person: p }: { person: CrmPerson }) {
  const { tenantPath } = useTenant();
  const name = p.name ?? "Unnamed contact";
  return (
    <Stack direction="row" spacing={1} sx={{ py: 0.75, alignItems: "center" }}>
      <Box sx={{ minWidth: 0, flexGrow: 1 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
          {p.master_contact_id ? (
            <Link component={RouterLink} to={tenantPath(`/contacts/${p.master_contact_id}`)} color="inherit">
              {name}
            </Link>
          ) : (
            name
          )}
        </Typography>
        {p.title && (
          <Typography variant="caption" sx={{ color: "text.secondary" }} noWrap component="div">
            {p.title}
          </Typography>
        )}
      </Box>
      {p.do_not_email && <Chip size="small" variant="outlined" color="warning" label="Do not email" />}
      <CrmPresenceChip crm={p} />
    </Stack>
  );
}

/** How many people show before "Show all". */
const PEOPLE_SHOWN = 5;

function PeopleList({ people }: { people: CrmPerson[] }) {
  const [all, setAll] = useState(false);
  const shown = all ? people : people.slice(0, PEOPLE_SHOWN);
  return (
    <Box>
      <Typography variant="overline" sx={{ color: "text.secondary" }}>
        People in HubSpot · {people.length}
      </Typography>
      {shown.map((p, i) => (
        <PersonRow key={p.master_contact_id ?? `unlinked-${i}`} person={p} />
      ))}
      {people.length > PEOPLE_SHOWN && (
        <Link component="button" variant="body2" onClick={() => setAll((v) => !v)}>
          {all ? "Show fewer" : `Show all ${people.length}`}
        </Link>
      )}
    </Box>
  );
}

/**
 * The tenant's own history with the company in HubSpot: the summary and the
 * people it knows there for anyone prospecting it, the calls, emails and
 * meetings behind it for CRM readers.
 */
export default function ProspectCrmSection({ prospect }: { prospect: ProspectDetail }) {
  const { user } = useAuth();
  const { selectedOrgId } = useOrganization();
  const canReadCrm = userHasOrgScope(user, selectedOrgId, ORG_CRM_READ_SCOPE);
  const crm = prospect.crm;
  const activity = useQuery({
    queryKey: keys.prospect.crmActivity(prospect.id),
    queryFn: () => api.getProspectCrmActivity(prospect.id),
    enabled: !!crm && canReadCrm,
  });
  const people = prospect.crm_people ?? [];
  if (!crm && people.length === 0) return null;
  return (
    <Box>
      <SectionHeading title="HubSpot" />
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack spacing={1}>
          {crm && (
            <>
              <Box>
                <CrmPresenceChip crm={crm} />
              </Box>
              <CrmPresenceDetails crm={crm} />
            </>
          )}
          {people.length > 0 && <PeopleList people={people} />}
          {canReadCrm &&
            (activity.isLoading ? (
              <CircularProgress size={16} />
            ) : (
              (activity.data?.length ?? 0) > 0 && (
                <Box sx={{ pt: 1 }}>
                  {activity.data!.slice(0, 10).map((a) => (
                    <ActivityRow key={a.id} activity={a} />
                  ))}
                </Box>
              )
            ))}
        </Stack>
      </Paper>
    </Box>
  );
}
